import cn from 'classnames';
import { FormikProps } from 'formik';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  DELAYED_SHIPPING_EXPIRATION,
  getDefaultExpirationDateInUTC,
  INTERNAL_NOTES,
  IS_TOGGLE_ON_HOLD_ENABLED,
  SEND_ON_HOLD,
} from '../../../constants/bucket';
import { DateFormatsEnum } from '../../../constants/date';
import { BUSINESS_REASON_ID } from '../../../constants/reasons';
import { endpoints } from '../../../constants/routing';
import useFetch from '../../../hooks/useFetch';
import { addBucketValue } from '../../../store/actions/bucket';
import { selectIsBusinessReasonRequired } from '../../../store/selectors/bucket';
import { selectBusinessReasonById, selectBusinessReasons } from '../../../store/selectors/reasons';
import { IEditSendFieldRequestPayload, ISummaryFormFields } from '../../../types/bucket';
import { ISelectorValue, NotificationListEnum } from '../../../types/shell';
import { formatDate } from '../../../utils/date';
import notification from '../../../utils/notification';
import { ActionButton } from '../buttons';
import { DateAndTimezonePicker, HoldUntilDateInput, Selector, Textarea } from '../inputs';
import { LabelWithEditButton } from '../labels';

import styles from './AdditionalSummaryForm.module.scss';

interface IProps {
  className?: string;
  form: FormikProps<ISummaryFormFields>;
  onChange?: (name: keyof ISummaryFormFields, value: string | boolean) => void;
  readOnly?: boolean;
  onHoldIsAllowed?: boolean;
  sendId?: string;
  isDelayedShipping?: boolean;
  isEditBusinessReasonsAllowed?: boolean;
  isEditInternalNotesAllowed?: boolean;
  internalNotes?: string;
}

const AdditionalSummaryForm = ({
  className,
  form,
  onChange,
  readOnly,
  onHoldIsAllowed,
  sendId,
  isDelayedShipping,
  isEditBusinessReasonsAllowed,
  isEditInternalNotesAllowed,
  internalNotes,
}: IProps) => {
  const dispatch = useDispatch();
  const reasons = useSelector(selectBusinessReasons);
  const businessReason = useSelector(selectBusinessReasonById(form ? form.values[BUSINESS_REASON_ID] : null));
  const isBusinessReasonRequired = useSelector(selectIsBusinessReasonRequired);

  const [isBusinessReasonEditMode, setIsBusinessReasonEditMode] = React.useState(false);
  const [isInternalNotesEditMode, setIsInternalNotesEditMode] = React.useState(false);
  const [tempBusinessReasonSelectedValue, setTempBusinessReasonSelectedValue] = React.useState<ISelectorValue | null>(
    null,
  );

  const [tempInternalNotesSelectedValue, setTempInternalNotesSelectedValue] = useState(internalNotes);

  const [currentBusinessReasonSelectedValue, setCurrentBusinessReasonSelectedValue] = React.useState({
    label: businessReason?.name!,
    value: businessReason?.uid!,
  });

  const [currentInternalNotesValue, setCurrentInternalNotesValue] = useState(internalNotes);

  const resolvedInternalNotesValue = !readOnly ? form.values[INTERNAL_NOTES] : currentInternalNotesValue;

  const reasonsOptions = useMemo(() => {
    if (!reasons) {
      return [];
    }
    return reasons.map(({ name, uid }) => {
      return {
        label: name,
        value: uid,
      };
    });
  }, [reasons]);

  const businessReasonSelectedValue = useMemo(() => {
    return reasonsOptions.find(({ value }) => value === businessReason?.uid);
  }, [reasonsOptions, businessReason]);

  const businessReasonError = useMemo(() => {
    return isBusinessReasonRequired && !readOnly && !businessReasonSelectedValue ? 'Business reason is required' : null;
  }, [isBusinessReasonRequired, readOnly, businessReasonSelectedValue]);

  const { make: editSendFieldRequest, isLoading: isEditSendFieldLoading } = useFetch<
    IEditSendFieldRequestPayload,
    never
  >({
    endpoint: endpoints.editSendField,
    showToastOnError: true,
    method: 'PATCH',
    useMonolithApi: true,
  });

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { name, value } = e.currentTarget;
      if (!name) {
        console.warn('No field name was specified');
        return;
      }
      if (typeof onChange === 'function') {
        onChange(name as keyof ISummaryFormFields, value);
      }
    },
    [onChange],
  );

  const handleSelectInputChange = useCallback(
    (name: keyof ISummaryFormFields, selected: ISelectorValue) => {
      const { value } = selected || {};
      if (onChange) {
        return onChange(name, value);
      }
    },
    [onChange],
  );

  const handleChangeOnHoldDate = useCallback(
    (value: string) => {
      onChange?.(SEND_ON_HOLD, value ? new Date(value).toISOString() : value);
    },
    [onChange],
  );

  const handleChangeExpirationDate = useCallback(
    (value: string) => {
      onChange?.(DELAYED_SHIPPING_EXPIRATION, value ? formatDate(value, DateFormatsEnum.FullDateAndTime) : value);
    },
    [onChange],
  );

  const handleToggleChange = useCallback(
    (value: boolean) => {
      dispatch(addBucketValue({ [IS_TOGGLE_ON_HOLD_ENABLED]: value }));
    },
    [dispatch],
  );

  const handleSelectorChange = useCallback(
    (value: ISelectorValue) => {
      if (!isBusinessReasonEditMode) {
        handleSelectInputChange(BUSINESS_REASON_ID, value);
      } else {
        setTempBusinessReasonSelectedValue(value);
      }
    },
    [handleSelectInputChange, isBusinessReasonEditMode],
  );

  const handleInternalNotesChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.currentTarget;
      if (!isInternalNotesEditMode) {
        handleChange(e);
      } else {
        setTempInternalNotesSelectedValue(value);
      }
    },
    [handleChange, isInternalNotesEditMode],
  );

  const isInternalNotesValueChanged = useMemo(() => {
    return tempInternalNotesSelectedValue !== resolvedInternalNotesValue;
  }, [tempInternalNotesSelectedValue, resolvedInternalNotesValue]);

  const handleApplyBusinessReasonChanges = useCallback(() => {
    if (tempBusinessReasonSelectedValue) {
      if (!sendId) {
        return;
      }
      editSendFieldRequest({
        pathParams: {
          id: sendId,
        },
        body: {
          key: BUSINESS_REASON_ID,
          value: tempBusinessReasonSelectedValue?.value!,
        },
      }).then(() => {
        setTempBusinessReasonSelectedValue(tempBusinessReasonSelectedValue);
        setCurrentBusinessReasonSelectedValue(tempBusinessReasonSelectedValue);
        setIsBusinessReasonEditMode(false);
        notification.success(NotificationListEnum.Success, { content: 'Business reason has been updated' });
      });
    }
  }, [sendId, tempBusinessReasonSelectedValue, editSendFieldRequest]);

  const handleApplyInternalNotesChanges = useCallback(() => {
    if (!sendId) {
      return;
    }
    editSendFieldRequest({
      pathParams: {
        id: sendId,
      },
      body: {
        key: INTERNAL_NOTES,
        value: tempInternalNotesSelectedValue as string,
      },
    }).then(() => {
      if (!readOnly) {
        onChange?.(INTERNAL_NOTES, tempInternalNotesSelectedValue as string);
      }

      setCurrentInternalNotesValue(tempInternalNotesSelectedValue);
      setIsInternalNotesEditMode(false);
      notification.success(NotificationListEnum.Success, { content: 'Internal notes have been updated' });
    });
  }, [sendId, tempInternalNotesSelectedValue, editSendFieldRequest, onChange]);

  const isValueChanged = useMemo(() => {
    return tempBusinessReasonSelectedValue
      ? !isEqual(currentBusinessReasonSelectedValue, tempBusinessReasonSelectedValue)
      : false;
  }, [currentBusinessReasonSelectedValue, tempBusinessReasonSelectedValue]);

  const showInternalNotes = useMemo(() => {
    return !readOnly || (readOnly && form.values[INTERNAL_NOTES]) || isEditInternalNotesAllowed;
  }, [readOnly, form.values[INTERNAL_NOTES], isEditInternalNotesAllowed]);

  const handleCancelBusinessReasonChanges = useCallback(() => {
    setIsBusinessReasonEditMode(false);
    setTempBusinessReasonSelectedValue(currentBusinessReasonSelectedValue || null);
  }, [currentBusinessReasonSelectedValue]);

  const handleCancelInternalNotesChanges = useCallback(() => {
    setIsInternalNotesEditMode(false);
  }, []);

  const renderEditButtons = useCallback(
    ({
      isChanged,
      isLoading,
      onApply,
      onCancel,
    }: {
      isChanged: boolean;
      isLoading: boolean;
      onApply: () => void;
      onCancel: () => void;
    }) => {
      return (
        <div className={styles.buttonsContainer}>
          {isChanged && (
            <ActionButton title="Apply changes" disabled={!isChanged || isLoading} onClick={onApply} inline />
          )}
          <ActionButton title="Cancel" onClick={onCancel} inline />
        </div>
      );
    },
    [],
  );

  useEffect(() => {
    if (!isInternalNotesEditMode) {
      setTempInternalNotesSelectedValue(resolvedInternalNotesValue);
    }
  }, [resolvedInternalNotesValue, isInternalNotesEditMode]);

  return (
    <form className={cn(styles.additionalSummaryForm, className)} onSubmit={form.handleSubmit}>
      <div className={styles.wrapper}>
        {!readOnly && onHoldIsAllowed && (
          <HoldUntilDateInput
            readOnly={readOnly}
            value={form.values[SEND_ON_HOLD]}
            onChange={handleChangeOnHoldDate}
            onToggleChange={handleToggleChange}
          />
        )}
        {!readOnly && isDelayedShipping && (
          <DateAndTimezonePicker
            value={form.values[DELAYED_SHIPPING_EXPIRATION]}
            onSubmit={(val) => handleChangeExpirationDate(val?.selectedDateString ?? '')}
            defaultValue={getDefaultExpirationDateInUTC().toISOString()}
          />
        )}
      </div>
      <div className={styles.businessReasonContainer}>
        <Selector
          name={BUSINESS_REASON_ID}
          isSearchable
          className={styles.input}
          closeMenuOnSelect
          value={businessReasonSelectedValue}
          tempValue={tempBusinessReasonSelectedValue}
          readOnly={readOnly}
          menuIsOpen={readOnly && !isBusinessReasonEditMode ? false : undefined}
          onChange={(value) => handleSelectorChange(value as ISelectorValue)}
          options={reasonsOptions}
          placeholder="Select business reason"
          required={isBusinessReasonRequired && !readOnly}
          error={businessReasonError}
          helperText={
            readOnly ? (
              <LabelWithEditButton
                isEditable={isEditBusinessReasonsAllowed}
                text="Business reason"
                onEdit={() => setIsBusinessReasonEditMode(true)}
                className={styles.labelContainer}
              />
            ) : (
              'Business reason'
            )
          }
          editMode={isBusinessReasonEditMode}
          isClearable={!isBusinessReasonEditMode}
          dataAction="select_business_reason"
        />
        {isBusinessReasonEditMode &&
          renderEditButtons({
            isChanged: isValueChanged,
            isLoading: isEditSendFieldLoading,
            onApply: handleApplyBusinessReasonChanges,
            onCancel: handleCancelBusinessReasonChanges,
          })}
      </div>
      {showInternalNotes && (
        <>
          <Textarea
            name={INTERNAL_NOTES}
            className={styles.textarea}
            onChange={handleInternalNotesChange}
            value={isInternalNotesEditMode ? tempInternalNotesSelectedValue : resolvedInternalNotesValue}
            helperText={
              !isEditInternalNotesAllowed ? (
                'Internal notes'
              ) : (
                <LabelWithEditButton
                  isEditable
                  text="Internal notes"
                  onEdit={() => setIsInternalNotesEditMode(true)}
                  className={styles.labelContainer}
                />
              )
            }
            placeholder="Put your note here"
            readOnly={readOnly && !isInternalNotesEditMode}
            dataAction="input_internal_notes"
          />
          {isInternalNotesEditMode &&
            renderEditButtons({
              isChanged: isInternalNotesValueChanged,
              isLoading: isEditSendFieldLoading,
              onApply: handleApplyInternalNotesChanges,
              onCancel: handleCancelInternalNotesChanges,
            })}
        </>
      )}
    </form>
  );
};

export default AdditionalSummaryForm;
